import React, { Component } from "react";
import { colors, gradients } from "./theme";

// Editable version of the numbered timeline.
//
// Two slides use it and both need a variable number of steps - some markets run
// only five Employer Brand steps, and the timeline dates and wording change per
// market. Certifiers can edit every field, reorder steps and add or remove
// them; the list is stored as JSON in one report content item.
//
// variant "steps" - numbered dots, title + description below (Employer Brand)
// variant "cards" - filled dots with a date above and a gradient card below
export default class V2EditableTimeline extends Component {
  _emit = (steps) => {
    this.props.onChange({
      target: {
        name: this.props.name,
        value: JSON.stringify(steps),
      },
    });
  };

  _onEdit = (index, field, value) => {
    const steps = [...this.props.steps];
    steps[index] = { ...steps[index], [field]: value };
    this._emit(steps);
  };

  _onAdd = () => {
    const blank =
      this.props.variant === "cards"
        ? { date: "", title: "" }
        : { title: "", text: "" };
    this._emit([...this.props.steps, blank]);
  };

  _onRemove = (index) => {
    this._emit(this.props.steps.filter((step, key) => key !== index));
  };

  _onMove = (index, direction) => {
    const steps = [...this.props.steps];
    const target = index + direction;
    if (target < 0 || target >= steps.length) return;
    [steps[index], steps[target]] = [steps[target], steps[index]];
    this._emit(steps);
  };

  _isEmpty = (step) =>
    !String(step.title || "").trim() &&
    !String(step.text || "").trim() &&
    !String(step.date || "").trim();

  render() {
    const { steps, variant = "steps", editable, addLabel } = this.props;
    const isCards = variant === "cards";

    // Empty steps are dropped from the printed report so no blank box is left.
    const visible = editable ? steps : steps.filter((s) => !this._isEmpty(s));

    if (!visible.length && !editable) {
      return null;
    }

    return (
      <div
        className={`report-v2__timeline report-v2__timeline--${
          isCards ? "cards" : "steps"
        }`}
      >
        <div className="report-v2__timeline-track">
          {visible.map((step, key) => (
            <div className="report-v2__timeline-node" key={key}>
              <span
                className="report-v2__timeline-dot"
                style={{
                  background: isCards ? colors.secondary : "#FFFFFF",
                  borderColor: colors.secondary,
                }}
              >
                {!isCards && key + 1}
              </span>
              {isCards && (
                <Editable
                  className="report-v2__timeline-date"
                  editable={editable}
                  value={step.date || ""}
                  placeholder="Month, year"
                  onChange={(value) => this._onEdit(key, "date", value)}
                />
              )}
            </div>
          ))}
        </div>

        <div className="report-v2__timeline-items">
          {visible.map((step, key) => (
            <div className="report-v2__timeline-item" key={key}>
              {editable && (
                <div className="report-v2__editable-tools">
                  <button type="button" onClick={() => this._onMove(key, -1)}>
                    ←
                  </button>
                  <button type="button" onClick={() => this._onMove(key, 1)}>
                    →
                  </button>
                  <button type="button" onClick={() => this._onRemove(key)}>
                    ×
                  </button>
                </div>
              )}

              {isCards ? (
                /* The number sits beside the card, not above it. */
                <div className="report-v2__timeline-card">
                  <span className="report-v2__timeline-index">{key + 1}</span>
                  <div
                    className="report-v2__card"
                    style={{ background: gradients.card }}
                  >
                    <Editable
                      editable={editable}
                      value={step.title || ""}
                      placeholder="Step"
                      onChange={(value) => this._onEdit(key, "title", value)}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <Editable
                    tag="strong"
                    editable={editable}
                    value={step.title || ""}
                    placeholder="Title"
                    onChange={(value) => this._onEdit(key, "title", value)}
                  />
                  <Editable
                    tag="p"
                    editable={editable}
                    value={step.text || ""}
                    placeholder="Description"
                    onChange={(value) => this._onEdit(key, "text", value)}
                  />
                </>
              )}
            </div>
          ))}
        </div>

        {editable && (
          <button
            type="button"
            className="report-v2__editable-add"
            onClick={this._onAdd}
          >
            {addLabel}
          </button>
        )}
      </div>
    );
  }
}

// contentEditable keeps the printed appearance identical while editing.
class Editable extends Component {
  _onBlur = (event) => {
    const value = event.target.innerText.trim();
    if (value !== this.props.value) {
      this.props.onChange(value);
    }
  };

  // Never re-render the node while the caret is inside it.
  shouldComponentUpdate(nextProps) {
    return (
      nextProps.editable !== this.props.editable ||
      (document.activeElement !== this.node &&
        nextProps.value !== this.props.value)
    );
  }

  render() {
    const {
      tag = "span",
      editable,
      value,
      placeholder,
      className,
    } = this.props;
    const Tag = tag;

    if (!editable) {
      return <Tag className={className}>{value}</Tag>;
    }

    return (
      <Tag
        ref={(node) => (this.node = node)}
        className={`${className || ""} report-v2__editable-text`}
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onBlur={this._onBlur}
      >
        {value}
      </Tag>
    );
  }
}
